/**
 * AppShell — authenticated top-level layout shell.
 *
 * Responsibilities:
 *  - Auth guard: redirects to "/" when the user is not signed in.
 *  - Fetches the project list when the user changes (on login).
 *  - Renders the top bar (logo, project menu, user controls).
 *  - Manages app-level modal state (Settings, Feature Request, Product Tour).
 *  - Manages policy/info page overlays (Privacy, Terms, Philosophy, Pricing).
 *  - Renders <Outlet> so nested routes (DashboardPage, ProjectPage) appear
 *    inside the authenticated shell.
 *
 * Context provided to nested routes via <Outlet context>:
 *  - showToast: display a transient notification banner.
 *  - openProjectMenu: open the ProjectMenu dropdown from child routes.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../../store/useStore';
import { fetchProjects, createProject, deleteProject, saveProjectOst } from '../../api/projects';
import { logoutUser } from '../../api/auth';
import { formatProjectToZey, parseZey } from '../../utils/zeyFormat';
import { isErrorResponse } from '../../utils/apiUtils';
import { createEmptyOst } from '../../utils/ostUtils';
import { UpgradeToast, usePricingStore, UpgradeMessageKey } from '../../pricing';
import { useProjectLimit } from '../../pricing/useProjectLimit';
import { AiUsageBadge } from '../../pricing/AiUsageBadge';

import ProjectMenu from '../ui/ProjectMenu';
import Settings from './Settings';
import FeatureRequestModal from '../modals/FeatureRequestModal';
import ProductTour, { TourPage } from './ProductTour';
import PolicyPage from './PolicyPage';
import PhilosophyPage from './PhilosophyPage';
import PricingPage from './PricingPage';
import TableView from './TableView';
import { ProjectModel } from '../../types';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Context provided to every nested route rendered within AppShell. */
export interface AppShellContext {
    /** Display a transient toast notification banner. */
    showToast: (msg: string) => void;
    /** Open the ProjectMenu dropdown from inside a nested route. */
    openProjectMenu: () => void;
    /** True while the initial project list is being fetched from the API. */
    projectsLoading: boolean;
}

// ─── Product Tour Pages ───────────────────────────────────────────────────────

/**
 * Ordered list of product-tour slides.
 */
const TOUR_PAGES: TourPage[] = [
    {
        title: 'Create a Project',
        description:
            'Create projects conveniently using the dropdown in the top left corner. Also delete the current project, or export as a .zey file for backup.',
        gifUrl: '/onboarding/zeytune-onboarding-1.gif',
    },
    {
        title: 'Build Your Outcome',
        description:
            'Every OST starts with an Outcome — the measurable goal your team is aiming for. Click "Add first Outcome" on the empty canvas to get started.',
        gifUrl: '/onboarding/zeytune-onboarding-2.gif',
    },
    {
        title: 'Adding Opportunities',
        description:
            'Hang Opportunity nodes off your Outcome. Each node can be expanded for deeper details. Easily position the panel anywhere in your workspace for convenience.',
        gifUrl: '/onboarding/zeytune-onboarding-3.gif',
    },
    {
        title: 'Connecting Insights to Opportunities (Ctrl + Drag)',
        description:
            'Add insights in the left pull-out panel, and connect them interactively to opportunities to build a robust information network.',
        gifUrl: '/onboarding/zeytune-onboarding-4.gif',
    },
    {
        title: 'Adding Solutions under Opportunities',
        description:
            'Add solutions under opportunities and break them down into experiments or stories using the familiar "As a / I want / So that" format, and track their status from draft through to Pushed to GitHub.',
        gifUrl: '/onboarding/zeytune-onboarding-5.gif',
    },
    {
        title: 'Drag Connect Nodes to Build Information Network',
        description:
            'Conveniently build intelligent relationships between nodes in different branches by pressing Ctrl while hovering on the node and then dragging to the target node.',
        gifUrl: '/onboarding/zeytune-onboarding-6.gif',
    },
];

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Top-level authenticated shell. Child routes render via <Outlet>.
 * If the user is not authenticated this component renders a redirect to "/".
 */
export default function AppShell() {
    const {
        user,
        projects,
        activeProject,
        outcome,
        opportunities,
        solutions,
        userStories,
        positions,
        nodePanelPosition,
        solutionChildType,
        connections,
        insights,
        insightLinks,
        triageItems,
        setProjects,
        clearProject,
        clearAuth,
        setOst,
        setProjectSharedData,
    } = useStore();

    const navigate = useNavigate();
    const location = useLocation();

    // Extract projectId from the URL so navigation works even when activeProject
    // is null (e.g. when the InsightsPage is mounted instead of ProjectPage).
    const projectIdFromUrl = location.pathname.match(/\/app\/project\/([^/]+)/)?.[1];

    // Which project sub-page is active, used to highlight the matching top-bar button.
    const isInsightsPage = location.pathname.endsWith('/insights');
    const isPlanPage = location.pathname.endsWith('/plan');
    const isOstsPage = !isInsightsPage && !isPlanPage;

    // ─── Font Loading ─────────────────────────────────────────────────────────
    const [fontReady, setFontReady] = useState(false);
    useEffect(() => {
        document.fonts.load('1em "Fleur De Leah"').then(() => setFontReady(true));
    }, []);

    // ─── Pricing Page Navigation Registration ────────────────────────────────
    const registerPricingNavigation = usePricingStore((s) => s.registerPricingNavigation);
    useEffect(() => {
        registerPricingNavigation(() => setInfoPage('pricing'));
    }, [registerPricingNavigation]);

    // ─── ?page=pricing query-param bootstrap ─────────────────────────────────
    // Allows the upgrade toast to open the pricing page in the same tab.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('page') === 'pricing') {
            setInfoPage('pricing');
            const clean = window.location.pathname;
            window.history.replaceState(null, '', clean);
        }
    }, []);

    // ─── Toast State ──────────────────────────────────────────────────────────
    const [toast, setToast] = useState<string | null>(null);
    const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** Display a transient toast notification for 3.5 seconds. */
    const showToast = useCallback((message: string): void => {
        setToast(message);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(null), 3500);
    }, []);

    // ─── Overlay Page State ───────────────────────────────────────────────────
    const [policyPage, setPolicyPage] = useState<'privacy' | 'terms' | 'impressum' | null>(null);
    const [infoPage, setInfoPage] = useState<'philosophy' | 'pricing' | null>(null);

    // ─── Modal State ──────────────────────────────────────────────────────────
    const [showSettings, setShowSettings] = useState(false);
    const [showProductTour, setShowProductTour] = useState(false);
    const [showFeatureRequest, setShowFeatureRequest] = useState(false);
    const [tableOpen, setTableOpen] = useState(false);

    // ─── Project Menu State ───────────────────────────────────────────────────
    const [projectMenuOpen, setProjectMenuOpen] = useState(false);
    const [projectName, setProjectName] = useState('');

    const showUpgradePrompt = usePricingStore((s) => s.showUpgradePrompt);
    const { ensureCanCreateProject } = useProjectLimit();

    // ─── Fetch Project List ───────────────────────────────────────────────────
    // Runs once when the user becomes available (login / page refresh).
    // Start as `true` so nested routes don't briefly render stale project
    // content before the initial fetch begins (prevents UI flicker).
    const [projectsLoading, setProjectsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        if (!user) return;

        // If projects are already present (e.g. restored from cache or
        // a previous fetch), avoid refetching — this also prevents the
        // loading spinner from briefly appearing again (React StrictMode
        // may mount effects twice in development).
        if (projects.length > 0) {
            setProjectsLoading(false);
            return;
        }

        const load = async () => {
            setProjectsLoading(true);
            const result = await fetchProjects();
            if (cancelled) return;
            setProjectsLoading(false);
            if (isErrorResponse(result)) {
                console.error(result.error);
            } else {
                const fetched = result as ProjectModel[];
                setProjects(fetched);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [user, setProjects, projects.length]);

    // ─── Auth Guard ───────────────────────────────────────────────────────────
    // Must be after all hooks to comply with Rules of Hooks.
    if (!user) {
        return <Navigate to='/' replace />;
    }

    // ─── Derived State ────────────────────────────────────────────────────────
    const isCurrentProjectEmpty =
        !outcome && opportunities.length === 0 && solutions.length === 0 && userStories.length === 0;

    // ─── Project Handlers ─────────────────────────────────────────────────────

    /** Create a new project using the projectName state, then navigate to it. */
    const handleCreateProject = async () => {
        if (!projectName.trim() || !user) return;
        if (!ensureCanCreateProject()) return;
        const result = await createProject(projectName.trim());
        if (isErrorResponse(result)) {
            showToast(result.error);
        } else {
            const project = result as ProjectModel;
            setProjects([...projects, project]);
            setProjectName('');
            // Navigate to the new project via the URL-driven route.
            navigate(`/app/project/${project._id}`);
        }
    };

    /** Navigate to an existing project's URL. */
    const handleSelectProject = (id: string) => {
        navigate(`/app/project/${id}`);
    };

    /** Delete the currently active project, then navigate back to the dashboard. */
    const handleDeleteProject = async () => {
        if (!activeProject || !user) return;
        if (!window.confirm(`Delete project '${activeProject.name}'? This cannot be undone.`)) return;
        const data = await deleteProject(activeProject._id);
        if (isErrorResponse(data)) {
            showToast(data.error);
        } else {
            const nextProjects = projects.filter((p) => p._id !== activeProject._id);
            setProjects(nextProjects);
            clearProject();
            // Route to dashboard; if projects remain navigate to the first one.
            if (nextProjects.length > 0) {
                navigate(`/app/project/${nextProjects[0]._id}`);
            } else {
                navigate('/app');
            }
        }
    };

    /** Export the currently active project as a .zey file. */
    const handleExportProject = () => {
        if (!activeProject) return;
        const projectForExport = {
            ...activeProject,
            ost: {
                outcome,
                opportunities,
                solutions,
                userStories,
                positions,
                nodePanelPosition,
                solutionChildType,
                connections,
                insights,
                insightLinks,
                triageItems,
            },
        };
        const zey = formatProjectToZey(projectForExport);
        const blob = new Blob([zey], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const safeName = (activeProject.name || 'project').replace(/[^a-zA-Z0-9-_ ]/g, '_');
        a.href = url;
        a.download = `${safeName}.zey`;
        a.click();
        URL.revokeObjectURL(url);
        showToast('Project exported as .zey successfully.');
    };

    /** Import a .zey file into a new project, then navigate to it. */
    const handleImportProject = async (file: File) => {
        if (!isCurrentProjectEmpty) {
            showToast('Cannot import into non-empty project. Clear nodes first.');
            return;
        }

        const text = await file.text();
        let parsed;
        try {
            parsed = parseZey(text);
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Invalid file format';
            showToast(`Import failed: ${message}`);
            return;
        }

        const projectNameFromFile = parsed.project?.name || file.name.replace(/\.zey$/i, '') || 'Imported project';

        const result = await createProject(projectNameFromFile);
        if (isErrorResponse(result)) {
            if (result.error && result.error.toLowerCase().includes('project limit')) {
                if (activeProject && isCurrentProjectEmpty) {
                    // Fallback: import into current empty project.
                    setOst(parsed.ost);
                    setProjectSharedData({
                        insights: parsed.ost.insights || [],
                        insightLinks: parsed.ost.insightLinks || [],
                        triageItems: parsed.ost.triageItems || [],
                    });
                    try {
                        await saveProjectOst(activeProject._id, parsed.ost);
                        setOst(parsed.ost);
                        setProjectSharedData({
                            insights: parsed.ost.insights || [],
                            insightLinks: parsed.ost.insightLinks || [],
                            triageItems: parsed.ost.triageItems || [],
                        });
                        showToast('Project limit reached; imported into current empty project.');
                    } catch {
                        showToast('Import failed during fallback update.');
                    }
                    return;
                }
                showUpgradePrompt(UpgradeMessageKey.ProjectLimit);
                return;
            }
            showToast(`Import failed: ${result.error}`);
            return;
        }

        const newProject = result as ProjectModel;
        setProjects([...projects, newProject]);

        // Save OST data to backend before navigating so ProjectPage fetches populated data.
        setOst(parsed.ost);
        setProjectSharedData({
            insights: parsed.ost.insights || [],
            insightLinks: parsed.ost.insightLinks || [],
            triageItems: parsed.ost.triageItems || [],
        });

        try {
            await saveProjectOst(newProject._id, parsed.ost);
        } catch {
            // Not fatal — local state is set; backend sync will retry on next save.
        }

        showToast('Project imported from .zey file');
        navigate(`/app/project/${newProject._id}`);
    };

    /** Sign the user out and navigate to the landing page. */
    const handleLogout = () => {
        logoutUser().catch(() => { /* best-effort */ });
        clearAuth();
        clearProject();
        setOst({
            ...createEmptyOst(),
        });
        navigate('/');
    };

    // ─── Overlay Early Returns ────────────────────────────────────────────────
    // These overlays replace the entire shell rather than layering on top,
    // preserving the existing UX behaviour while staying inside the auth guard.

    if (policyPage) {
        return <PolicyPage page={policyPage} onBack={() => setPolicyPage(null)} />;
    }
    if (infoPage === 'philosophy') {
        return <PhilosophyPage onBack={() => setInfoPage(null)} />;
    }
    if (infoPage === 'pricing') {
        return <PricingPage onBack={() => setInfoPage(null)} />;
    }

    // ─── Render ───────────────────────────────────────────────────────────────

    const outletContext: AppShellContext = { showToast, openProjectMenu: () => setProjectMenuOpen(true), projectsLoading };

    return (
        <div className='app-shell'>
            {/* ── Top bar ─────────────────────────────────────────────────── */}
            <div className='app-topbar'>
                <div className='app-leftarea'>
                    <div className='app-logoarea'>
                        {/* Logo navigates to the dashboard */}
                        <button
                            className={`app-logo app-logo-btn ${fontReady ? '' : 'font-pending'}`}
                            onClick={() => navigate('/app')}
                            aria-label='Go to dashboard'
                            title='Dashboard'
                        >
                            Zeytune
                        </button>
                    </div>
                    <span className='app-logo-separator'>|</span>
                    <span className='current-project-tag'>{activeProject?.name || 'No project'}</span>
                    <AiUsageBadge />
                </div>

                <div className='app-statsouter'>
                    {location.pathname.startsWith('/app/project') && (
                        <div className='app-top-actions'>
                            {/* ── Page navigation: Home / OSTs / Insights / Plan ── */}
                            <button
                                className='app-top-action-btn'
                                onClick={() => navigate('/app')}
                                title='Home'
                                aria-label='Home'
                            >
                                <svg width='25' height='25' viewBox='-1 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true' focusable='false'>
                                    <path d='M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V10.5z' stroke='currentColor' strokeWidth='2.8' strokeLinecap='round' strokeLinejoin='round' />
                                </svg>
                            </button>

                            <button
                                className={`app-top-action-btn${isOstsPage ? ' app-top-action-btn--active' : ''}`}
                                onClick={() => {
                                    const id = activeProject?._id ?? projectIdFromUrl;
                                    if (!id) return;
                                    navigate(`/app/project/${id}`);
                                }}
                                title='OSTs'
                                aria-label='OSTs'
                            >
                                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true' focusable='false'>
                                    <rect x='9' y='2' width='6' height='4' rx='0.5' stroke='currentColor' strokeWidth='2.4' />
                                    <rect x='2' y='18' width='6' height='4' rx='0.5' stroke='currentColor' strokeWidth='2.4' />
                                    <rect x='16' y='18' width='6' height='4' rx='0.5' stroke='currentColor' strokeWidth='2.4' />
                                    <path d='M12 6v4M12 10H5v4M12 10h7v4' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' />
                                </svg>
                            </button>

                            <button
                                className={`app-top-action-btn${isInsightsPage ? ' app-top-action-btn--active' : ''}`}
                                onClick={() => {
                                    const id = activeProject?._id ?? projectIdFromUrl;
                                    if (!id) return;
                                    navigate(`/app/project/${id}/insights`);
                                }}
                                title='Insights'
                                aria-label='Insights'
                            >
                                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true' focusable='false'>
                                    <path d='M9 21h6' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' />
                                    <path d='M9 17.5V15c-1.5-1-3-3-3-6a6 6 0 0 1 12 0c0 3-1.5 5-3 6v2.5' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' />
                                    <path d='M9 17.5h6' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' />
                                </svg>
                            </button>

                            <button
                                className={`app-top-action-btn${isPlanPage ? ' app-top-action-btn--active' : ''}`}
                                onClick={() => {
                                    const id = activeProject?._id ?? projectIdFromUrl;
                                    if (!id) return;
                                    navigate(`/app/project/${id}/plan`);
                                }}
                                title='Plan'
                                aria-label='Plan'
                            >
                                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true' focusable='false'>
                                    <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='2.4' />
                                    <circle cx='12' cy='12' r='5' stroke='currentColor' strokeWidth='2.4' />
                                    <circle cx='12' cy='12' r='1' fill='currentColor' />
                                </svg>
                            </button>

                            {/* ── Divider ── */}
                            <span className='app-top-actions-divider' aria-hidden='true' />

                            {/* ── Project config / table view ── */}
                            <ProjectMenu
                                projects={projects}
                                activeProject={activeProject}
                                projectMenuOpen={projectMenuOpen}
                                setProjectMenuOpen={setProjectMenuOpen}
                                projectName={projectName}
                                setProjectName={setProjectName}
                                handleSelectProject={handleSelectProject}
                                handleDeleteProject={handleDeleteProject}
                                handleCreateProject={handleCreateProject}
                                handleExportProject={handleExportProject}
                                handleImportProject={handleImportProject}
                                importDisabled={!isCurrentProjectEmpty}
                            />

                            <button className='app-top-action-btn' onClick={() => setTableOpen(true)} title='Open table view'>
                                <svg width='13' height='11' viewBox='0 0 13 11' fill='none'>
                                    <line x1='0' y1='1' x2='13' y2='1' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                                    <line x1='0' y1='5.5' x2='13' y2='5.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                                    <line x1='0' y1='10' x2='13' y2='10' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>

                <div className='app-rightarea'>
                    <span className='app-user-tag'>{user?.name || 'User'}</span>
                    <button className='app-settings-btn' onClick={handleLogout}>
                        Logout
                    </button>
                    {/* Settings are project-scoped; only show when a project is open */}
                    {location.pathname.startsWith('/app/project') && (
                        <button className='app-settings-btn' onClick={() => setShowSettings(true)}>
                            Settings
                        </button>
                    )}
                    <button className='app-green-btn' onClick={() => setShowFeatureRequest(true)}>
                        Submit Request / Problem
                    </button>
                    <button
                        className='app-help-btn'
                        onClick={() => setShowProductTour(true)}
                        aria-label='Open product tour'
                        title='Help / Product Tour'
                    >
                        ?
                    </button>
                </div>
            </div>

            {/* ── Nested Route Content (Dashboard or Project canvas) ────────── */}
            <Outlet context={outletContext} />

            {/* ── Modals ───────────────────────────────────────────────────── */}
            {showSettings && <Settings onClose={() => setShowSettings(false)} onAccountDeleted={handleLogout} />}
            {showFeatureRequest && <FeatureRequestModal onClose={() => setShowFeatureRequest(false)} />}
            {showProductTour && (
                <ProductTour
                    pages={TOUR_PAGES}
                    onClose={() => {
                        setShowProductTour(false);
                        if (user?.id) {
                            localStorage.setItem(`tour_seen_${user.id}`, '1');
                        }
                    }}
                />
            )}
            {tableOpen && <TableView onClose={() => setTableOpen(false)} />}

            {/* ── Toast notification ────────────────────────────────────────── */}
            {toast && <div className='toast'>{toast}</div>}

            {/* ── Pricing upgrade prompt ────────────────────────────────────── */}
            <UpgradeToast />
        </div>
    );
}
